/**
 * The preview's sample data: what fills the placeholders and what decides which optional
 * parts show.
 *
 * The subtleties here are load-bearing:
 *
 * - **Fillable fields come from a baseline render, not from the body alone.** A
 *   template's own `{{ … }}` placeholders are only part of it: the layout and the
 *   fragments it includes contribute their own (`Event_start_date` and friends), and
 *   those only appear once the Twig has actually been rendered.
 * - **Condition variables are separated from value variables.** "What decides whether a
 *   part shows" and "what fills text in" are different questions, and mixing them made
 *   the panel unreadable.
 *
 * - **Nothing here outlives the drawer.** Sample values used to be written to
 *   `localStorage` per template, on the theory that authoring scaffolding is worth
 *   keeping. In practice a preview reopened days later still showed a customer's name
 *   and appointment date as if they were the template's own content — owner-stated, too
 *   sticky — so closing the drawer now drops the fill, and picking the record again is
 *   two clicks.
 *
 * **What this no longer does, deliberately:** it held a JSON textarea's contents and a
 * set of per-language translation *overrides*. The JSON box is gone — owner-stated, the
 * fields are the interface — and translations are now edited for real through the
 * translation field, against the partner catalog, rather than shadowed for one preview.
 * An override that only changed what *you* saw was the opposite of what proof-reading an
 * email in thirteen languages is for.
 */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { extractTransKeys, extractVariablePaths } from './insertion';
import { useLastGoodRender } from './lastGoodRender';
import { renderTwigPreview } from './twigRenderer';

/**
 * Where a bulk-filled set of values came from — the record the user picked.
 *
 * `values` is kept alongside the label so a field can say not only "this came
 * from Anna Alt" but also what her value was, which is what makes an edited
 * field restorable instead of merely overwritten.
 */
export interface AppliedSampleSource {
  /** How the record was labelled in the picker — the customer's name. */
  label: string;
  /** What it filled: variable path → the record's own value. */
  values: Record<string, string>;
}

export interface SampleDataInput {
  /** The body being previewed. */
  body: string;
  /** Base context from the host (the journey theme), overlaid by sample values. */
  previewContext?: Record<string, string>;
  /** Partner translations resolved for the active language. */
  translations?: Record<string, string>;
  fragments?: Record<string, string>;
  layoutKey?: string | null;
}

export interface SampleDataState {
  /** Variable paths the template actually fills. */
  placeholderPaths: string[];
  /** Variable paths that decide whether parts show. */
  conditionPaths: string[];
  /** Translation keys the body pipes through `|trans`. */
  transKeys: string[];
  /** Per-path sample values the user typed. */
  values: Record<string, string>;
  setValue: (path: string, value: string) => void;
  /** Fill many paths at once — a real lead's or appointment's variables. */
  applyValues: (
    values: Record<string, string>,
    source?: { label: string },
  ) => void;
  /** Drop what the last record filled in, keeping hand-typed values. */
  clearValues: () => void;
  /** The record the current bulk-filled values came from, if any. */
  appliedSource: AppliedSampleSource | null;
  /** Everything merged, ready for `renderTwigPreview`. */
  mergedContext: Record<string, string>;
}

const STORAGE_PREFIX = 'emailSampleData:';

/**
 * Remove the sample values earlier versions wrote per template.
 *
 * They are no longer read, but they hold real customer data — a name, an email, an
 * appointment date pulled from a lead — and leaving that sitting in a shared machine's
 * browser storage because the code that wrote it was deleted is not a cleanup anyone
 * would choose. Wrapped like every other storage access: it throws outright in some
 * privacy modes.
 */
function purgeStoredSampleData(): void {
  try {
    const stale: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(STORAGE_PREFIX)) stale.push(key);
    }
    for (const key of stale) window.localStorage.removeItem(key);
  } catch {
    // Storage blocked — there is nothing to clean up that we could reach anyway.
  }
}

/**
 * Bookkeeping for values that arrived in bulk from a real record.
 *
 * Clearing the selection has to remove exactly what that record filled and
 * nothing else — a value someone typed by hand before reaching for a real
 * appointment is theirs, and wiping it would be the kind of small betrayal that
 * makes people stop using a control. So the applied keys are remembered and
 * only those are withdrawn, both on clear and when a second record replaces the
 * first.
 */
export function useAppliedSampleValues(
  setValues: Dispatch<SetStateAction<Record<string, string>>>,
  initialSource: AppliedSampleSource | null = null,
) {
  // Rendered (the fields show which record filled them), so this half is state.
  const [appliedSource, setAppliedSource] =
    useState<AppliedSampleSource | null>(initialSource);
  // The withdrawal half is a ref: applying must not depend on a render having
  // happened, or two quick picks would each drop the same stale keys.
  const appliedKeys = useRef<string[]>(
    Object.keys(initialSource?.values ?? {}),
  );

  const withdraw = (current: Record<string, string>, keys: string[]) => {
    const next = { ...current };
    for (const key of keys) delete next[key];
    return next;
  };

  // The keys to withdraw are read *before* the updater is queued and passed in
  // by value. Reading `appliedKeys.current` inside the updater would see the
  // keys of the record being applied — React runs the updater after this
  // function has already moved the ref on — and nothing would ever be removed.
  const applyValues = useCallback(
    (incoming: Record<string, string>, source?: { label: string }) => {
      const previous = appliedKeys.current;
      appliedKeys.current = Object.keys(incoming);
      setValues((current) => ({ ...withdraw(current, previous), ...incoming }));
      setAppliedSource({ label: source?.label ?? '', values: incoming });
    },
    [setValues],
  );

  const clearValues = useCallback(() => {
    const previous = appliedKeys.current;
    appliedKeys.current = [];
    setValues((current) => withdraw(current, previous));
    setAppliedSource(null);
  }, [setValues]);

  /**
   * Adopt a source without touching `values` — for a host that replaces both at
   * once (a rehydrate from storage, or a reset when the previewed template
   * changes). Going through `applyValues` there would re-fill the values it is
   * being handed.
   */
  const resetSource = useCallback((source: AppliedSampleSource | null) => {
    appliedKeys.current = Object.keys(source?.values ?? {});
    setAppliedSource(source);
  }, []);

  return { appliedSource, applyValues, clearValues, resetSource };
}

export function useSampleData({
  body,
  previewContext,
  translations,
  fragments,
  layoutKey,
}: SampleDataInput): SampleDataState {
  // Sample values live and die with the editor: the host remounts it per template, and
  // closing the drawer is what drops them. See the note at the top of the file.
  const [values, setValues] = useState<Record<string, string>>({});

  const { appliedSource, applyValues, clearValues } =
    useAppliedSampleValues(setValues);

  useEffect(purgeStoredSampleData, []);

  /**
   * A baseline render with no sample values, purely to discover what the template —
   * *including its layout and fragments* — asks for.
   */
  const freshBaseline = useMemo(
    () =>
      renderTwigPreview(body, {
        context: previewContext,
        translations,
        fragments,
        layoutKey,
      }),
    [body, previewContext, translations, fragments, layoutKey],
  );

  /**
   * Held on the last renderable body for the same reason the preview is: the rows come
   * out of what the *render* asked for, so an unfinished `{{` emptied the panel of
   * every fragment- and layout-contributed variable while the user was typing one.
   */
  const baseline = useLastGoodRender(freshBaseline);

  const { placeholderPaths, conditionPaths } = useMemo(() => {
    const placeholders = extractVariablePaths(body);
    for (const path of baseline.placeholderVariables) {
      if (!placeholders.includes(path)) placeholders.push(path);
    }
    return {
      placeholderPaths: placeholders,
      conditionPaths: baseline.conditionVariables.filter(
        (path) => !placeholders.includes(path),
      ),
    };
  }, [body, baseline]);

  const transKeys = useMemo(() => extractTransKeys(body), [body]);

  /**
   * Precedence: the host's context (the journey theme) first, then the per-field values.
   * The field a user typed into wins, which is the only ordering that does not surprise.
   */
  const mergedContext = useMemo(() => {
    const merged = { ...(previewContext ?? {}) };
    for (const [path, sample] of Object.entries(values)) {
      if (sample !== '') merged[path] = sample;
    }
    return merged;
  }, [previewContext, values]);

  const setValue = useCallback((path: string, value: string) => {
    setValues((current) => ({ ...current, [path]: value }));
  }, []);

  return {
    placeholderPaths,
    conditionPaths,
    transKeys,
    values,
    setValue,
    applyValues,
    clearValues,
    appliedSource,
    mergedContext,
  };
}
